import React, { FC, useCallback, useMemo } from 'react';
import { PositionFromTokenId } from '@app/hooks/v3/usePositions';
import { Box, Flex, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import clsx from 'clsx';
import s from '@app/screens/positions/components/PositionsList/PositionsList.module.scss';
import { PoolTokensCell } from '@app/screens/pools/components/PoolTokensCell';
import { usePool } from '@app/hooks/v3/usePool';
import { useSinglePoolQuery } from '@app/hooks/v3/useSingelPoolQuery';
import { INITIAL_POOL_FEE, Position } from '@cryptoalgebra/kim-sdk';
import { usePoolFeeDataQuery } from '@app/hooks/v3/usePoolFeeDataQuery';
import { usePositionApr } from '@app/hooks/v3/usePositionApr';
import { useNativePriceQuery } from '@app/hooks/v3/useNativePriceQuery';
import { formatUnits } from 'ethers/lib/utils';
import { useDerivedMintInfo } from '@app/state/mintStore';
import { CollectFeesModalV3 } from '@app/screens/positions/components/CollectFeesModalV3';
import { usePositionFees } from '@app/hooks/v3/usePositionFees';
import { Icon } from '@app/components/Icon';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useMedia, useToggle } from 'react-use';
import { PositionDetails } from '@app/screens/positions/components/PositionDetails';
import { FormattedPosition } from '@app/types/formatted-position';
import { AnimateHeight } from '@app/components/AnimateHeight';
import { formatUSD } from '@app/helpers/formatUSD';
import { OLD_WEETH_TOKEN } from '@app/constants/tokens';
import { Tooltip } from '@app/components/Tooltip';
import { isOldWethPool } from '@app/helpers/positions';
import { getCurrencySymbol } from '@app/helpers/currency';
import { Token } from '@app/types/token';
import { AmountSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/AmountSection';
import { CompositionSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/CompositionSection';
import { APRSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/APRSection';

interface Props {
  data: PositionFromTokenId;
}

const variants = {
  open: {
    opacity: 1,
    height: 'auto'
  },
  collapsed: { opacity: 0, height: 0 }
};

export const PositionsListRowV3: FC<Props> = ({ data }) => {
  const isMobile = useMedia('(max-width: 960px)', false);
  const [open, toggleOpen] = useToggle(false);
  const poolId = data.pool;
  const { data: bundle } = useNativePriceQuery();
  const [, poolEntity] = usePool(poolId);
  const { data: poolInfo } = useSinglePoolQuery({
    poolId
  });
  const { data: poolFeeData } = usePoolFeeDataQuery({
    poolId
  });

  const formatLiquidityUSD = useCallback(
    (position: Position) => {
      if (!poolInfo || !bundle) return 0;

      const amount0USD =
        Number(position.amount0.toSignificant()) *
        Number(poolInfo.token0.derivedMatic) *
        Number(bundle.maticPriceUSD);
      const amount1USD =
        Number(position.amount1.toSignificant()) *
        Number(poolInfo.token1.derivedMatic) *
        Number(bundle.maticPriceUSD);

      return amount0USD + amount1USD;
    },
    [bundle, poolInfo]
  );

  const position = useMemo(() => {
    if (!poolEntity) {
      return;
    }

    return {
      positionId: data.tokenId,
      position: new Position({
        pool: poolEntity,
        liquidity: data.liquidity.toString(),
        tickLower: Number(data.tickLower),
        tickUpper: Number(data.tickUpper)
      })
    };
  }, [
    data.liquidity,
    data.tickLower,
    data.tickUpper,
    data.tokenId,
    poolEntity
  ]);

  const positionApr = usePositionApr(
    poolId,
    position?.position,
    poolInfo,
    poolFeeData,
    bundle?.maticPriceUSD
  );

  const positionsFees = usePositionFees(poolEntity ?? undefined, data.tokenId);

  const positionFeesUSD = useMemo(() => {
    if (!positionsFees || !poolInfo) {
      return;
    }

    const { amount0, amount1 } = positionsFees;

    const fees0USD = amount0
      ? Number(amount0.toSignificant()) * Number(poolInfo.token0Price)
      : 0;
    const fees1USD = amount1
      ? Number(amount1.toSignificant()) * Number(poolInfo.token1Price)
      : 0;

    return fees0USD + fees1USD;
  }, [poolInfo, positionsFees]);

  const formattedPosition: FormattedPosition | undefined = useMemo(() => {
    if (!position || !poolEntity) {
      return;
    }

    return {
      id: position.positionId,
      outOfRange:
        poolEntity.tickCurrent < position.position.tickLower ||
        poolEntity.tickCurrent > position.position.tickUpper,
      range: `${position.position.token0PriceLower.toFixed()} — ${position.position.token0PriceUpper.toFixed()}`,
      liquidityUSD: formatLiquidityUSD(position.position),
      liquidity: formatUnits(position.position.liquidity.toString(), 18),
      feesUSD: positionFeesUSD ?? 0,
      positionsFees,
      apr: positionApr,
      position: position.position,
      composition: {
        amount0: position.position.amount0,
        amount1: position.position.amount1
      }
    } satisfies FormattedPosition;
  }, [
    formatLiquidityUSD,
    poolEntity,
    position,
    positionApr,
    positionFeesUSD,
    positionsFees
  ]);

  const mintInfo = useDerivedMintInfo(
    position?.position.amount0.currency,
    position?.position.amount1.currency,
    poolId,
    INITIAL_POOL_FEE,
    position?.position.amount0.currency,
    position?.position || undefined
  );

  const isOldWethPosition = isOldWethPool(
    poolInfo?.token0.id,
    poolInfo?.token1.id
  );

  function renderContent() {
    if (!poolEntity || !formattedPosition || !poolInfo) {
      return;
    }

    return (
      <>
        {isMobile ? (
          <VStack gap={3} width="100%">
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <PoolTokensCell
                token0={poolInfo.token0}
                token1={poolInfo.token1}
                version={4}
              />
              {isOldWethPosition && (
                <Tooltip
                  className={s.transferTooltip}
                  placement="bottom-start"
                  tooltip="weETH has upgraded their token standard, which requires swapping from the old to new weETH.
Please select transfer to automatically swap and create position in the new pool."
                >
                  <Icon
                    name="infoCircle"
                    style={{ color: '#eed202', marginLeft: '8px' }}
                  />
                </Tooltip>
              )}
            </HStack>
            {formattedPosition.outOfRange ? (
              <HStack>
                <Icon
                  name="infoCircle"
                  style={{ width: '16px', height: '16px', color: '#FFAD8F' }}
                />
                <Text fontSize="12px" color="txt.accent">
                  Out of range
                </Text>
              </HStack>
            ) : null}
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                Amount
              </Text>
              <AmountSection
                amount={formattedPosition.liquidity ?? 0}
                usdAmount={formattedPosition.liquidityUSD ?? 0}
              />
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                Composition
              </Text>
              <CompositionSection
                amount0={
                  formattedPosition.composition.amount0.toSignificant() ?? ''
                }
                amount1={
                  formattedPosition.composition.amount1.toSignificant() ?? ''
                }
                token0Address={poolInfo.token0.id}
                token1Address={poolInfo.token1.id}
                token0Symbol={poolInfo.token0.symbol}
                token1Symbol={poolInfo.token1.symbol}
              />
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                APR
              </Text>
              <APRSection
                totalApr={formattedPosition.apr}
                farmBaseApr={0}
                lockBonusApr={0}
              />
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              {position && !!positionFeesUSD ? (
                <CollectFeesModalV3
                  positionFeesUSD={positionFeesUSD}
                  mintInfo={mintInfo}
                  positionId={position?.positionId}
                />
              ) : (
                <div />
              )}
              <IconButton aria-label="expand" onClick={toggleOpen}>
                {open ? (
                  <ChevronUpIcon width="24px" height="24px" />
                ) : (
                  <ChevronDownIcon width="24px" height="24px" />
                )}
              </IconButton>
            </HStack>
          </VStack>
        ) : (
          <>
            <Flex width="30%">
              <PoolTokensCell
                token0={poolInfo.token0}
                token1={poolInfo.token1}
                version={4}
              />
              {isOldWethPosition && (
                <Tooltip
                  className={s.transferTooltip}
                  placement="bottom-start"
                  tooltip="weETH has upgraded their token standard, which requires swapping from the old to new weETH.
Please select transfer to automatically swap and create position in the new pool."
                >
                  <Icon
                    name="infoCircle"
                    style={{ color: '#eed202', marginLeft: '8px' }}
                  />
                </Tooltip>
              )}
            </Flex>
            <Flex width="10%">
              {formattedPosition.outOfRange ? (
                <HStack>
                  <Icon
                    name="infoCircle"
                    style={{ width: '16px', height: '16px', color: '#FFAD8F' }}
                  />
                  <Text fontSize="12px" color="txt.accent">
                    Out of range
                  </Text>
                </HStack>
              ) : null}
            </Flex>
            <Flex width="10%">
              <AmountSection
                amount={formattedPosition.liquidity ?? 0}
                usdAmount={formattedPosition.liquidityUSD ?? 0}
              />
            </Flex>
            <Flex width="10%">
              <CompositionSection
                amount0={
                  formattedPosition.composition.amount0.toSignificant() ?? ''
                }
                amount1={
                  formattedPosition.composition.amount1.toSignificant() ?? ''
                }
                token0Address={poolInfo.token0.id}
                token1Address={poolInfo.token1.id}
                token0Symbol={poolInfo.token0.symbol}
                token1Symbol={poolInfo.token1.symbol}
              />
            </Flex>
            <Flex width="5%">
              <APRSection
                totalApr={formattedPosition.apr}
                farmBaseApr={0}
                lockBonusApr={0}
              />
            </Flex>
            <Flex width="20%" justifyContent="flex-end" gap={2}>
              {position && !!positionFeesUSD && (
                <CollectFeesModalV3
                  positionFeesUSD={positionFeesUSD}
                  mintInfo={mintInfo}
                  positionId={position?.positionId}
                />
              )}
              <IconButton aria-label="expand" onClick={toggleOpen}>
                {open ? (
                  <ChevronUpIcon width="24px" height="24px" />
                ) : (
                  <ChevronDownIcon width="24px" height="24px" />
                )}
              </IconButton>
            </Flex>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <Box
        className={clsx(s.row, s.rowMobile, {
          [s.open]: open
        })}
      >
        {renderContent()}
      </Box>
      <AnimateHeight
        variants={variants}
        ease="easeOut"
        duration={0.3}
        isVisible={open}
      >
        {poolEntity && formattedPosition && (
          <PositionDetails
            pool={poolEntity}
            position={formattedPosition}
            mintInfo={mintInfo}
            poolInfo={poolInfo}
          >
            details here
          </PositionDetails>
        )}
      </AnimateHeight>
    </>
  );
};
